import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { MapPin, Navigation } from "lucide-react";
import { toLatin, toGreek, capitalize, smartToGreek, brandExceptions, smartCityToGreek, smartAreaToGreek } from "../utils/textDictionary";
import { queuePendingAction } from '../utils/offlineDB';

// Configure Leaflet marker icons to use custom purple marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: purpleMarker,
    iconUrl: purpleMarker,
    shadowUrl: markerShadow,
});

//reusable component for the Form fields
const FieldRow = ({ label, children }) => (
    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full">
        <span className="w-32 font-semibold text-custom-letters text-left">{label}:</span>
        <div className="flex-1">{children}</div>
    </div>
);

//main component
const AddStore = () => {
    const navigate = useNavigate();
    const { accessToken, language, user, userRole } = useContext(AuthContext);

    const translations = {
        el: {
            title: "Προσθήκη νέου καταστήματος",
            storeName: "Kατάστημα",
            city: "Πόλη",
            area: "Περιοχή",
            road: "Οδός",
            trackMe: "Εντοπισμός θέσης",
            saveStore: "Αποθήκευση",
            successTitle: "Το κατάστημα αποθηκεύτηκε!",
            successMsg: "Οι πληροφορίες του καταστήματος αποθηκεύτηκαν με επιτυχία.",
            errorLocation: "Σφάλμα κατά τη λήψη της τοποθεσίας.",
            errorSave: "Αποτυχία αποθήκευσης καταστήματος",
            unknownLocation: "Άγνωστη τοποθεσία",
            ok: "Εντάξει",
            errorTitle: "Σφάλμα",
            offlineTitle: "Αποθηκεύτηκε για αργότερα",
            offlineMsg: "Είστε εκτός σύνδεσης. Το κατάστημα θα αποθηκευτεί αυτόματα όταν επανασυνδεθείτε."
        },
        en: {
            title: "Add new store",
            storeName: "Store name",
            city: "City",
            area: "Area",
            road: "Road",
            trackMe: "Track me",
            saveStore: "Save store",
            successTitle: "Store saved!",
            successMsg: "The store information have been successfully saved.",
            errorLocation: "Error getting location.",
            errorSave: "Failed to save store",
            unknownLocation: "Unknown location",
            ok: "OK",
            errorTitle: "Error",
            offlineTitle: "Saved for later",
            offlineMsg: "You are offline. The store will be saved automatically when you reconnect."
        }
    };

    const t = translations[language] || translations.el;
    const storeSuccessPath = userRole === "admin" ? "/adminstores" : "/customermenu";

    // Store form field values for store name, city, area, and road
    const [form, setForm] = useState({
        store_name: "",
        city: "",
        area: "",
        road: "",
    });

    // Store the selected location (latitude, longitude, address) from map interaction
    const [location, setLocation] = useState(null);
    const [defaultCenter, setDefaultCenter] = useState({ lat: 37.9838, lng: 23.7275 });
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [offlineQueuedPopup, setOfflineQueuedPopup] = useState(false);
    const [errorPopup, setErrorPopup] = useState(null);

    // Validate form: ensure all fields are filled and location is selected
    const isFormInvalid = !form.store_name.trim() ||
        !form.city.trim() ||
        !form.area.trim() ||
        !form.road.trim() ||
        !location;

    // Update form state when user types in input fields
    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Auto-center map and update marker based on entered address with debounced geocoding API calls
    useEffect(() => {
        // Combine non-empty address fields into a search query
        const addressParts = [form.road, form.area, form.city].filter(part => part.trim().length > 0);

        // Initiate geocoding search when enough address details are entered
        if (addressParts.length >= 1 && form.city.length > 2) {
            const delayDebounceFn = setTimeout(async () => {
                try {
                    // Format address parts into comma-separated query string for Nominatim API (e.g. "Patision Ave, Kypseli, Athens")
                    const searchQuery = addressParts.join(", ");

                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1`
                    );
                    const data = await res.json();

                    if (data && data.length > 0) {
                        const { lat, lon } = data[0];
                        const newCoords = { lat: parseFloat(lat), lng: parseFloat(lon) };

                        // Update map center to new coordinates
                        setDefaultCenter(newCoords);

                        // Update marker position and get official address from Nominatim API
                        setLocation(prev => ({
                            ...prev,
                            lat: newCoords.lat,
                            lng: newCoords.lng,
                            address: data[0].display_name // Use official address from Nominatim API
                        }));
                    }
                } catch (err) {
                    console.error("Geocoding error:", err);
                }
            }, 1200); // 1.2s debounce delay allows user to finish typing address before API call

            return () => clearTimeout(delayDebounceFn);
        }
    }, [form.city, form.area, form.road]); // Re-run when any address field changes

    const fetchAddress = async (lat, lng) => {
        setLoading(true);
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`);
            const data = await res.json();
            const street = data.address?.road || "";
            const houseNumber = data.address?.house_number || "";
            const suburb = data.address?.suburb || data.address?.neighbourhood || data.address?.village || "";
            const city = data.address?.city || data.address?.town || "";
            const cleanRoad = `${street} ${houseNumber}`.trim();
            const areaInfo = suburb || city;

            setLocation({
                lat, lng,
                address: cleanRoad && areaInfo ? `${cleanRoad}, ${areaInfo}` : (cleanRoad || areaInfo || t.unknownLocation),
            });
            setDefaultCenter({ lat, lng });
        } catch (err) {
            // Offline or Nominatim unavailable — still place the marker using raw coordinates
            // The address will be the coordinate pair; it can be corrected later
            setLocation({
                lat,
                lng,
                address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            });
            setDefaultCenter({ lat, lng });
        } finally { setLoading(false); }
    };

    // Get user's current GPS position and reverse-geocode to get street address
    const handleTrackMe = () => {
        if (navigator.geolocation) {
            setLoading(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => fetchAddress(pos.coords.latitude, pos.coords.longitude),
                () => { setLoading(false); setErrorPopup({ title: t.errorTitle, msg: t.errorLocation }); }
            );
        }
    };

    // Render marker at clicked location and fetch address details from coordinates
    const LocationMarker = () => {
        useMapEvents({ click(e) { fetchAddress(e.latlng.lat, e.latlng.lng); } });
        return location ? <Marker position={[location.lat, location.lng]} /> : null;
    };

    // Smoothly animate map view to new center coordinates when location is updated
    const MapController = ({ center }) => {
        const map = useMap();
        useEffect(() => { if (center) map.flyTo(center, 18); }, [center, map]);
        return null;
    };

    // Format and submit store data to backend with automatic translations in Greek and English
    const handleSubmit = async () => {
        if (isFormInvalid) return;

        setLoading(true);

        const payload = {
            store_name_el: language === 'el'
                ? capitalize(form.store_name)
                : smartToGreek(form.store_name).includes(' ') || brandExceptions[form.store_name.toLowerCase().trim()]
                    ? smartToGreek(form.store_name)
                    : capitalize(smartToGreek(form.store_name)),

            city_el: language === 'el'
                ? capitalize(form.city)
                : smartCityToGreek(form.city),
            area_el: language === 'el'
                ? capitalize(form.area)
                : smartAreaToGreek(form.area),
            road_el: capitalize(language === 'el' ? form.road : toGreek(form.road)),

            store_name_en: capitalize(language === 'en' ? form.store_name : toLatin(form.store_name)),
            city_en: capitalize(language === 'en' ? form.city : toLatin(form.city)),
            area_en: capitalize(language === 'en' ? form.area : toLatin(form.area)),
            road_en: capitalize(language === 'en' ? form.road : toLatin(form.road)),

            latitude: parseFloat(location.lat),
            longitude: parseFloat(location.lng),
            store_address: location.address,
        };

        // If offline, queue the store for replay when the user reconnects
        if (!navigator.onLine) {
            await queuePendingAction({
                type: 'ADD_STORE',
                url: `${process.env.REACT_APP_BACKEND_URL}/addstore/add`,
                method: 'POST',
                body: payload,
                userId: user?.user_id,
            });
            setLoading(false);
            setOfflineQueuedPopup(true);
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/addstore/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || t.errorSave);

            setShowSuccess(true);
        } catch (err) {
            setErrorPopup({ title: t.errorTitle, msg: t.errorSave });
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-custom-background flex flex-col">
            <Header />
            <div className="flex-1 flex flex-row relative text-custom-letters">
                <ProfileSidebar />
                <main id="addstore-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 pt-20 md:pt-10 xl:ml-20 text-center md:text-left">
                    <div className="w-full max-w-3xl mx-auto bg-custom-header rounded-3xl shadow-xl p-6 md:p-12">
                        <h2 className="text-lg md:text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2 whitespace-nowrap">
                            <MapPin className="text-custom-grad1" /> {t.title}
                        </h2>
                        <div className="flex flex-col gap-5">
                            <FieldRow label={t.storeName}>
                                <input name="store_name" value={form.store_name} onChange={handleInputChange} className="w-full bg-custom-background px-4 py-2 rounded-full outline-none border border-transparent focus:border-custom-grad1" />
                            </FieldRow>
                            <FieldRow label={t.city}>
                                <input name="city" value={form.city} onChange={handleInputChange} className="w-full bg-custom-background px-4 py-2 rounded-full outline-none border border-transparent focus:border-custom-grad1" />
                            </FieldRow>
                            <FieldRow label={t.area}>
                                <input name="area" value={form.area} onChange={handleInputChange} className="w-full bg-custom-background px-4 py-2 rounded-full outline-none border border-transparent focus:border-custom-grad1" />
                            </FieldRow>
                            <FieldRow label={t.road}>
                                <input name="road" value={form.road} onChange={handleInputChange} className="w-full bg-custom-background px-4 py-2 rounded-full outline-none border border-transparent focus:border-custom-grad1" />
                            </FieldRow>

                            <div className="mt-4">
                                <div className="relative rounded-2xl overflow-hidden border-2 border-custom-grad1/30">
                                    <button type="button"
                                        title={t.trackMe}
                                        onClick={handleTrackMe} className="absolute top-4 right-4 z-[1000] p-3 rounded-full bg-custom-header border border-custom-grad1">
                                        <Navigation size={20} />
                                    </button>
                                    <MapContainer center={defaultCenter} zoom={18} style={{ height: "350px", width: "100%" }}>
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        <LocationMarker />
                                        <MapController center={defaultCenter} />
                                    </MapContainer>
                                </div>
                            </div>

                            <div className="flex justify-center mt-8">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isFormInvalid || loading}
                                    className={`font-bold px-12 py-3 rounded-full transition-all duration-300 ${isFormInvalid || loading
                                        ? "bg-gray-500 cursor-not-allowed opacity-50 text-white"
                                        : "bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white hover:scale-105"
                                        }`}
                                >
                                    {t.saveStore}
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            {showSuccess && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
                    <div role="dialog" aria-modal="true" aria-labelledby="addstore-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                        <h3 id="addstore-popup-title" className="text-lg font-semibold text-custom-letters mb-3">{t.successTitle}</h3>
                        <p className="text-sm text-custom-letters mb-6">{t.successMsg}</p>
                        <button onClick={() => navigate(storeSuccessPath)} className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">OK</button>
                    </div>
                </div>
            )}

            {offlineQueuedPopup && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
                    <div role="dialog" aria-modal="true" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                        <h3 className="text-lg font-semibold text-custom-letters mb-3">{t.offlineTitle}</h3>
                        <p className="text-sm text-custom-letters mb-6">{t.offlineMsg}</p>
                        <button
                            onClick={() => { setOfflineQueuedPopup(false); navigate(storeSuccessPath); }}
                            className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
                            OK
                        </button>
                    </div>
                </div>
            )}

            {errorPopup && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
                    <div role="dialog" aria-modal="true" aria-labelledby="addstore-error-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
                        <h3 id="addstore-error-title" className="text-lg font-semibold text-custom-letters mb-3">{errorPopup.title}</h3>
                        <p className="text-sm text-custom-letters mb-6">{errorPopup.msg}</p>
                        <button
                            onClick={() => setErrorPopup(null)}
                            className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
                            {t.ok}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
export default AddStore;